import React, { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { useDispatch } from "./redux/store";

import PopupWidget from "./PopupWidget";
import { SentryTest } from "./pages";

import { fetchDomainOnlyData } from "./functions/fetchDomainOnlyData";
import { fetchFullSiteData } from "./functions/fetchFullSiteData";
import { parseQueryString } from "./utils";

import { setShops } from "./redux/slices/shopSlice";
import { addAppointment, updateAppointment } from "./redux/slices/appointmentSlice";
import { BookingStage } from "./constants/bookingStages";

const decodeParentLocation = (raw) => {
  if (!raw) return "";
  try {
    return decodeURIComponent(raw);
  } catch (e) {
    return raw;
  }
};

function App() {
  const [loading, setLoading] = useState(true);
  const isSentryTestRoute = window.location.pathname === "/sentry-test";

  const dispatch = useDispatch();

  let domain = "";
  let groupName = "";
  let shopId = "";

  const queryString = parseQueryString(window.location);
  // Prefer the parent page's URL (forwarded by dcPortal.js) over the iframe's
  // own search, which only contains the params we set on the iframe URL.
  const parentLocationFromQuery = decodeParentLocation(queryString?.parentLocation);
  const sourceQueryString =
    parentLocationFromQuery || window.location.search || "";

  if (queryString?.domain) {
    domain = queryString?.domain;
  }
  if (queryString?.groupname) {
    groupName = queryString?.groupname;
  }
  if (queryString?.shopId) {
    shopId = queryString?.shopId;
  }
  const rwg_token = queryString?.rwg_token;

  // Setup message listener for postMessage API
  useEffect(() => {
    if (isSentryTestRoute) return undefined;

    const handleMessage = (event) => {
      if (event.data && event.data.type === "DC_PARENT_LOCATION") {
        const next = decodeParentLocation(event.data.parentLocation);
        if (next) {
          dispatch(updateAppointment({ updates: { sourceQueryString: next } }));
        }
        return;
      }
      const { siteID } = event.data;
      if (siteID) {
        // Use the siteID to load specific content or perform actions
        // Example: Fetch data based on the siteID
        fetchFullSiteData(domain, siteID)
          .then((data) => {
            const appointmentData = {
              stage: BookingStage.ServiceSelection,
              domain: domain,
              shopData: data,
              rwg_token: rwg_token,
              bailed: false,
              gaUtmSent: false,
              entryStage: BookingStage.ServiceSelection,
              specId: 1,
              sourceQueryString: sourceQueryString,
            };

            dispatch(addAppointment(appointmentData));
          })
          .catch((error) => {
            console.error("Error fetching site config (postMessage):", error);
          });
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [dispatch, domain, rwg_token, isSentryTestRoute]);

  useEffect(() => {
    if (isSentryTestRoute) {
      setLoading(false);
      return;
    }

    if (domain && shopId) {
      fetchFullSiteData(domain, shopId)
        .then((data) => {
          const appointmentData = {
            stage: BookingStage.ServiceSelection,
            domain: domain,
            shopData: data,
            shopId: shopId,
            rwg_token: rwg_token,
            bailed: false,
            gaUtmSent: false,
            entryStage: BookingStage.ServiceSelection,
            specId: 0,
            sourceQueryString: sourceQueryString,
          };

          // Commit the shop before leaving the loading state, as React 18's
          // legacy root did: PopupWidget then initializes GA4 before
          // ServiceOptions mounts and sends its first event.
          flushSync(() => dispatch(addAppointment(appointmentData)));
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching site config:", error);
          setLoading(false);
        });
    } else if ((domain && groupName) || (domain && groupName === "")) {
      fetchDomainOnlyData(domain, groupName).then((data) => {
        dispatch(setShops(data));

        const appointmentData = {
          stage: BookingStage.Location,
          domain: domain,
          shopId: shopId,
          rwg_token: rwg_token,
          bailed: false,
          gaUtmSent: false,
          entryStage: BookingStage.Location,
          specId: 0,
          sourceQueryString: sourceQueryString,
        };

        dispatch(addAppointment(appointmentData));
        setLoading(false);
      });
    } else {
      console.log("Error fetching site config");
    }
  }, [dispatch, domain, shopId, rwg_token, isSentryTestRoute]);

  if (isSentryTestRoute) {
    return <SentryTest />;
  }

  return (
    <div
      id="widget-container"
      style={{ width: "100vw", minHeight: "1px", display: "hidden" }}
    >
      <PopupWidget loading={loading} />
    </div>
  );
}

export default App;
